import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Plus, Mic, Square, X, Loader2, Image as ImageIcon, Camera, Paperclip, Video, FileText, Upload } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import { BACKEND_URL } from "../../lib/api";
import { compressImage } from "../../lib/imageCompress";

/**
 * ChatComposer — Wave 19 #85, Wave 23 (paste / drag-drop / multi-stage gallery),
 * Wave 26 (2026-02-16) voice rewrite for ChatGPT-like UX.
 *
 * The text + voice + multi-file input bar used in both /chat/:tid and the
 * embedded EntityChatPanel.
 *
 *  • Text: type & press Enter (Shift+Enter for newline) or tap Send.
 *  • Voice: TAP to start, TAP again to stop. No hold-to-record (which
 *    on iOS Safari fired onMicUp after the lightest finger movement
 *    and silently dropped 100ms blobs — Dirk bug 2026-05-16). Hard cap
 *    raised to 15 min. On stop we Whisper-transcribe synchronously and
 *    send the result as a TEXT message so the transcript shows in the
 *    bubble immediately — like ChatGPT. If Whisper fails the original
 *    audio is still sent as a fallback (zero data loss).
 *  • Multi-file: + button opens a sheet with Photo/Video/File/Camera —
 *    each one accepts MULTIPLE selections.
 *  • Paste (Wave 23): Cmd/Ctrl+V on the text input pulls images from the
 *    clipboard into the staging tray.
 *  • Drag & drop (Wave 23): drop files anywhere on the composer to stage
 *    them. A full-bleed drop hint appears while a drag is in flight.
 *  • Staging tray (Wave 23): preview pending files before send. The send
 *    button uploads them sequentially; the typed text (if any) is sent
 *    as a trailing caption, matching the WhatsApp send-then-caption
 *    flow. Recipients see them as a 2-col gallery via ChatMessage burst
 *    grouping (consecutive image/video msgs from the same author within
 *    60 s render in one bubble).
 */

const VOICE_CAP_MS = 15 * 60 * 1000; // 15 minutes — long-form dictation
const MIN_VOICE_BLOB_BYTES = 1500;   // anything smaller is a phantom tap
const MAX_STAGE_ITEMS = 20;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB / file

function _kindForType(ct) {
  if (!ct) return "file";
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("audio/")) return "audio";
  return "file";
}

export default function ChatComposer({ threadId, onSent, onBatchDone, prefill, disabled, apiBase = "/chats" }) {
  const [text, setText] = useState("");
  // `prefill` = { text, n } — bump `n` to drop new text into the box (still fully editable).
  useEffect(() => {
    if (prefill?.text) { setText(prefill.text); setTimeout(() => taRef.current?.focus(), 0); }
  }, [prefill?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const [showAttachSheet, setShowAttachSheet] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedSec, setRecordedSec] = useState(0);
  const [stage, setStage] = useState([]);            // Wave 23: pending files awaiting send
  const [dragActive, setDragActive] = useState(false);
  // [Iter 341 — 2026-02-25] Per-send upload progress. Tracks
  // { currentIndex, total, filename, pct }. `null` = idle. Displayed
  // as a WhatsApp-style progress pill above the composer so techs
  // don't wonder whether an upload is actually progressing.
  // Dirk 2026-02-25: "if upload takes a while and it's not instantly,
  // like on WhatsApp, then we need to see the status bar as well".
  const [uploadProgress, setUploadProgress] = useState(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const capTimerRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const taRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Auto-resize textarea
  useEffect(() => {
    if (!taRef.current) return;
    taRef.current.style.height = "auto";
    taRef.current.style.height = Math.min(taRef.current.scrollHeight, 160) + "px";
  }, [text]);

  // Revoke object URLs on unmount / stage change so we don't leak blobs.
  useEffect(() => {
    return () => {
      stage.forEach((s) => { if (s.previewUrl) try { URL.revokeObjectURL(s.previewUrl); } catch { /* noop */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _backendUrlFor = (uploadRec) => {
    // [Iter 323 — 2026-02-20] Stop baking the auth token into the
    // stored `upload_url`. Prior behaviour was
    //   `${base}/api/uploads/{id}?auth={live_token_at_send_time}`
    // which fossilised the JWT — once that token expired (or the
    // JWT_SECRET was rotated) every historical chat image 401'd
    // and rendered as a broken icon (Dirk report, 2026-02-20).
    // The receiver-side render helper (`chatMediaSrc` in
    // ChatMessage.jsx + the lightbox mapper in ChatThreadView.jsx)
    // rebuilds the URL from `msg.upload_id` + the CURRENT session
    // token at every render, so stored URLs never need a token.
    const base = BACKEND_URL || "";
    return `${base}/api/uploads/${uploadRec.id}`;
  };

  // ─── Stage helpers ────────────────────────────────────────────────────
  const _addToStage = useCallback((rawFiles) => {
    if (!rawFiles || rawFiles.length === 0) return;
    const files = Array.from(rawFiles);
    setStage((prev) => {
      const room = MAX_STAGE_ITEMS - prev.length;
      if (room <= 0) {
        toast.error(`Up to ${MAX_STAGE_ITEMS} files per send.`);
        return prev;
      }
      const accepted = [];
      let droppedSize = 0;
      for (const f of files.slice(0, room)) {
        if (f.size > MAX_FILE_SIZE) { droppedSize += 1; continue; }
        const kind = _kindForType(f.type);
        const previewUrl = (kind === "image" || kind === "video") ? URL.createObjectURL(f) : null;
        accepted.push({
          id: `stage-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          file: f,
          name: f.name || (kind === "image" ? "pasted-image.png" : "file"),
          size: f.size,
          kind,
          previewUrl,
        });
      }
      if (droppedSize > 0) toast.error(`${droppedSize} file(s) over 50 MB — skipped.`);
      if (files.length > room) toast.info(`Only first ${room} added (max ${MAX_STAGE_ITEMS}).`);
      return [...prev, ...accepted];
    });
  }, []);

  const _removeStaged = (id) => {
    setStage((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target?.previewUrl) try { URL.revokeObjectURL(target.previewUrl); } catch { /* noop */ }
      return prev.filter((s) => s.id !== id);
    });
  };

  const _clearStage = () => {
    stage.forEach((s) => { if (s.previewUrl) try { URL.revokeObjectURL(s.previewUrl); } catch { /* noop */ } });
    setStage([]);
  };

  // ─── Send logic ───────────────────────────────────────────────────────
  /**
   * If there are staged files: upload each in turn (one chat message per
   * upload) → if there's text too, send it last as a caption. The
   * receiving render layer groups consecutive media messages into one
   * gallery bubble, so the user perceives "one batch".
   * Otherwise: send the text as a single message (legacy path).
   */
  const sendNow = async () => {
    if (busy || disabled) return;
    const v = (text || "").trim();
    if (stage.length === 0 && !v) return;

    setBusy(true);
    let mediaSent = 0;
    let lastMsg = null;
    try {
      // 1. Upload + post each staged file.
      //    Images are compressed client-side first to dodge the
      //    Cloudflare 520 we hit on large phone photos.
      const total = stage.length;
      for (let i = 0; i < stage.length; i += 1) {
        const s = stage[i];
        // [Iter 341] Show "Uploading 1 of 3 · filename" with a live
        // progress bar. Percent is the AXIOS onUploadProgress bytes
        // ratio — updates as the phone streams bytes to the server.
        setUploadProgress({
          currentIndex: i + 1,
          total,
          filename: s.name,
          pct: 0,
        });
        try {
          const compressed = await compressImage(s.file);
          const fd = new FormData();
          fd.append("file", compressed);
          const { data: up } = await api.post("/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 0,  // chat attachment upload — no wall-clock cap
            onUploadProgress: (evt) => {
              if (!evt || !evt.total) return;
              const pct = Math.round((evt.loaded / evt.total) * 100);
              setUploadProgress((prev) => (prev && prev.currentIndex === i + 1
                ? { ...prev, pct: Math.min(99, pct) }
                : prev));
            },
          });
          // Mark this file 100% before posting the chat message so the
          // pill snaps to full for a beat, matching WhatsApp UX.
          setUploadProgress((prev) => (prev && prev.currentIndex === i + 1
            ? { ...prev, pct: 100 }
            : prev));
          const { data: msg } = await api.post(`${apiBase}/${threadId}/messages`, {
            kind: s.kind,
            upload_id: up.id,
            upload_url: _backendUrlFor(up),
            upload_filename: s.name,
            upload_content_type: (compressed.type || s.file.type || up.content_type),
            upload_size_bytes: compressed.size,
          });
          onSent?.(msg);
          lastMsg = msg;
          mediaSent += 1;
        } catch (e) {
          toast.error(`Failed: ${s.name} — ${e?.response?.data?.detail || e.message}`);
        }
      }
      // 2. Trailing caption (if any)
      if (v) {
        try {
          const { data } = await api.post(`${apiBase}/${threadId}/messages`, { kind: "text", text: v });
          onSent?.(data);
          lastMsg = data;
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not send caption.");
        }
      }
      // One reply for the whole batch (all files + caption), not one per file.
      if (lastMsg) onBatchDone?.(lastMsg);
    } finally {
      _clearStage();
      setText("");
      setUploadProgress(null);
      if (mediaSent > 0) toast.success(`${mediaSent} file${mediaSent === 1 ? "" : "s"} sent.`);
      setBusy(false);
    }
  };

  // Legacy entry from + sheet → just stage and let the user review
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setShowAttachSheet(false);
    _addToStage(files);
  };

  // ─── Paste & drag-and-drop (Wave 23) ──────────────────────────────────
  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      _addToStage(files);
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) {
      dragCounterRef.current += 1;
      setDragActive(true);
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length > 0) _addToStage(dropped);
  };

  // ─── Voice notes — Iter 393 (2026-02-28 rewrite) ────────────────
  // Dirk: "This is crucial because oftentimes we will send voice
  // notes or recordings of sounds which we are hearing, such as a
  // pump starting or something. We need the audio, not only the
  // transcription."
  //
  // Rewrite of the Wave-26 "ChatGPT-style" flow: previously we called
  // Whisper synchronously, sent the transcript as a plain text
  // message, and DISCARDED the audio unless Whisper failed. That
  // erased the actual sound the tech was trying to capture (pump
  // squeals, motor sounds, etc.).
  //
  // New flow (WhatsApp-style):
  //   1. On stop, immediately upload the blob to R2.
  //   2. Immediately POST an audio-kind message so the audio bubble
  //      appears in the thread the second the recording stops. No
  //      waiting on Whisper.
  //   3. Backend's `_transcribe_audio_bg` runs Whisper async and
  //      stamps `transcription` + `transcription_status` on the SAME
  //      message record — so the pair (audio ↔ transcription) is
  //      atomic. Nothing can interleave between them.
  //   4. ChatThreadView already polls every 15s and re-fetches
  //      existing messages so the transcription footer appears under
  //      the audio bubble as soon as Whisper finishes (typically
  //      2-6s for a 15-30s clip).
  const [transcribing, setTranscribing] = useState(false); // kept only for the disabled-mic guard

  const _sendVoiceNote = async (blob, dur) => {
    // Instant upload + audio message. Backend will transcribe async.
    setTranscribing(true);
    try {
      const ext = (blob.type || "").includes("mp4") ? "m4a" : "webm";
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      const { data: msg } = await api.post(`${apiBase}/${threadId}/messages`, {
        kind: "audio",
        upload_id: up.id,
        upload_url: _backendUrlFor(up),
        upload_filename: file.name,
        upload_content_type: file.type,
        upload_duration_s: dur,
        upload_size_bytes: file.size,
      });
      onSent?.(msg);
      onBatchDone?.(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send voice note.");
    } finally {
      setTranscribing(false);
    }
  };

  const stopRecording = ({ autoSend = true, silent = false } = {}) => {
    try {
      if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
        mediaRecRef.current._autoSend = autoSend;
        mediaRecRef.current._silent = silent;
        mediaRecRef.current.stop();
      }
    } catch (e) { /* noop */ }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (capTimerRef.current) { clearTimeout(capTimerRef.current); capTimerRef.current = null; }
    setRecording(false);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice notes not supported on this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = "";
      for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
        if (window.MediaRecorder.isTypeSupported(t)) { mimeType = t; break; }
      }
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const dur = (Date.now() - startedAtRef.current) / 1000;
        if (rec._autoSend === false) return;                  // explicit cancel
        if (blob.size < MIN_VOICE_BLOB_BYTES) {
          // Tap-too-short safety — don't silently drop, tell the user
          // so they know to try again. This is the iOS bug Dirk hit:
          // the old hold-to-record gesture fired onMicUp on the
          // smallest finger movement and dropped microscopic blobs.
          if (!rec._silent) toast.error("Recording too short — tap the mic to start, tap again to stop.");
          return;
        }
        await _sendVoiceNote(blob, dur);
      };
      rec.start();
      mediaRecRef.current = rec;
      startedAtRef.current = Date.now();
      setRecording(true);
      setRecordedSec(0);
      tickRef.current = setInterval(() => {
        setRecordedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      capTimerRef.current = setTimeout(() => {
        toast.info("Recording hit the 15-minute cap — sending now.");
        stopRecording({ autoSend: true });
      }, VOICE_CAP_MS);
    } catch (e) {
      if (e?.name === "NotAllowedError") {
        toast.error("Microphone access denied. Allow it in your browser settings.");
      } else {
        toast.error(e?.message || "Could not start recording.");
      }
    }
  };

  const cancelRecording = () => stopRecording({ autoSend: false });

  // Tap-to-toggle — single onClick handler replaces the old hold-to-record
  // onPointerDown/Up pair that was misbehaving on iOS.
  const onMicTap = () => {
    if (disabled || busy || transcribing) return;
    if (recording) {
      stopRecording({ autoSend: true });
    } else {
      startRecording();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────
  const hasStage = stage.length > 0;
  const canSendByText = (text || "").trim().length > 0;
  const sendActive = hasStage || canSendByText;

  return (
    <div
      className="border-t border-white/10 bg-[#0c1018]/80 backdrop-blur p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] relative shrink-0"
      data-testid="chat-composer"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drop overlay (Wave 23) */}
      {dragActive && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none rounded border-2 border-dashed border-[#1FB6C1] bg-[#1FB6C1]/10 backdrop-blur-[2px]"
          data-testid="chat-drop-overlay"
        >
          <div className="flex flex-col items-center gap-2 text-[#7fe5ec]">
            <Upload className="w-6 h-6" />
            <div className="text-sm font-bold">Drop to attach</div>
            <div className="text-[10px] text-[#a0aabf]">Up to {MAX_STAGE_ITEMS} files · 50 MB each</div>
          </div>
        </div>
      )}

      {/* Hidden file inputs (kept for + sheet picker) */}
      <input ref={photoInputRef} type="file" multiple accept="image/*"
        className="hidden" onChange={(e) => handleFiles(e.target.files)}
        data-testid="chat-input-photo" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={(e) => handleFiles(e.target.files)}
        data-testid="chat-input-camera" />
      <input ref={videoInputRef} type="file" multiple accept="video/*"
        className="hidden" onChange={(e) => handleFiles(e.target.files)}
        data-testid="chat-input-video" />
      <input ref={fileInputRef} type="file" multiple
        className="hidden" onChange={(e) => handleFiles(e.target.files)}
        data-testid="chat-input-file" />

      {showAttachSheet && (
        <div
          className="absolute bottom-full left-2 mb-2 hv-card p-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1 z-10 shadow-2xl"
          data-testid="chat-attach-sheet"
        >
          {[
            { Icon: ImageIcon, label: "Photos", ref: photoInputRef, testid: "chat-attach-photo" },
            { Icon: Camera, label: "Camera", ref: cameraInputRef, testid: "chat-attach-camera" },
            { Icon: Video, label: "Videos", ref: videoInputRef, testid: "chat-attach-video" },
            { Icon: Paperclip, label: "File", ref: fileInputRef, testid: "chat-attach-file" },
          ].map(({ Icon, label, ref, testid }) => (
            <button
              key={label}
              onClick={() => ref.current?.click()}
              className="hv-btn hv-btn-ghost text-xs flex flex-col items-center justify-center gap-1 py-2 min-w-[88px]"
              data-testid={testid}
            >
              <Icon className="w-4 h-4" />
              <span className="text-[10px]">{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Stage tray (Wave 23) */}
      {/* [Iter 341 — 2026-02-25] Upload progress pill.
          Renders above the composer while attachments are being
          uploaded so techs on slow SA cellular see actual byte-level
          progress instead of a mystery "is it doing anything?" pause.
          Dirk 2026-02-25: "if upload takes a while and it's not
          instantly, like on WhatsApp, then we need to see the status
          bar as well on those two areas." */}
      {uploadProgress && (
        <div
          className="mb-2 px-2.5 py-2 rounded-md border border-sky-500/40 bg-sky-500/10"
          data-testid="chat-upload-progress"
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[11px] text-sky-100 flex items-center gap-1.5 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-300 animate-pulse shrink-0" />
              <span className="truncate" data-testid="chat-upload-progress-label">
                Uploading {uploadProgress.currentIndex} of {uploadProgress.total}
                {uploadProgress.filename ? ` · ${uploadProgress.filename.slice(0, 30)}` : ""}
              </span>
            </span>
            <span
              className="text-[10px] font-mono tabular-nums text-sky-200 shrink-0"
              data-testid="chat-upload-progress-pct"
            >
              {uploadProgress.pct}%
            </span>
          </div>
          <div className="h-1 rounded-full overflow-hidden bg-sky-950/60">
            <div
              className="h-full bg-sky-400 transition-[width] duration-200"
              style={{ width: `${Math.max(3, uploadProgress.pct)}%` }}
            />
          </div>
        </div>
      )}

      {hasStage && (
        <div
          className="mb-2 px-2 py-2 rounded-md bg-white/[0.03] border border-white/10"
          data-testid="chat-stage-tray"
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf]">
              {stage.length} attachment{stage.length === 1 ? "" : "s"} ready
            </div>
            <button
              type="button"
              onClick={_clearStage}
              className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] hover:text-rose-300 transition"
              data-testid="chat-stage-clear"
            >
              Clear all
            </button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1">
            {stage.map((s) => (
              <div
                key={s.id}
                className="relative shrink-0 group"
                data-testid={`chat-stage-item-${s.id}`}
              >
                {(s.kind === "image" || s.kind === "video") && s.previewUrl ? (
                  s.kind === "image" ? (
                    <img
                      src={s.previewUrl}
                      alt={s.name}
                      className="w-16 h-16 rounded object-cover bg-black/30 border border-white/15"
                    />
                  ) : (
                    <video
                      src={s.previewUrl}
                      className="w-16 h-16 rounded object-cover bg-black/50 border border-white/15"
                      muted
                    />
                  )
                ) : (
                  <div className="w-16 h-16 rounded bg-white/[0.06] border border-white/10 flex flex-col items-center justify-center gap-1 px-1">
                    <FileText className="w-5 h-5 text-[#a0aabf]" />
                    <span className="text-[8px] text-[#a0aabf] text-center truncate w-full" title={s.name}>
                      {s.name.length > 10 ? s.name.slice(0, 8) + "…" : s.name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => _removeStaged(s.id)}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#0a0d14] border border-white/20 hover:border-rose-400 hover:text-rose-300 text-[#cbd2df] flex items-center justify-center text-[10px] shadow-md"
                  data-testid={`chat-stage-remove-${s.id}`}
                  aria-label={`Remove ${s.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {recording ? (
        <div className="flex items-center gap-3 px-2 py-2" data-testid="chat-recording-ui">
          <span className="w-3 h-3 rounded-full bg-rose-500 animate-pulse" />
          <span className="text-sm font-mono tabular-nums">
            {String(Math.floor(recordedSec / 60)).padStart(2, "0")}:{String(recordedSec % 60).padStart(2, "0")}
          </span>
          <span className="text-[10px] text-[#a0aabf] flex-1">Recording — tap stop to transcribe & send, X to cancel.</span>
          <button onClick={cancelRecording} className="hv-btn hv-btn-ghost text-xs" data-testid="chat-cancel-recording">
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={() => stopRecording({ autoSend: true })}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="chat-stop-send"
          >
            <Square className="w-3 h-3 fill-current" /> Send
          </button>
        </div>
      ) : transcribing ? (
        <div className="flex items-center gap-3 px-2 py-2" data-testid="chat-transcribing-ui">
          <Loader2 className="w-4 h-4 animate-spin text-[#1FB6C1]" />
          <span className="text-sm text-[#a0aabf] flex-1">Transcribing your voice note…</span>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => setShowAttachSheet((v) => !v)}
            disabled={disabled || busy}
            className="hv-btn hv-btn-ghost text-xs shrink-0"
            data-testid="chat-attach-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
          <textarea
            ref={taRef}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendNow();
              }
            }}
            onFocus={() => {
              try {
                setTimeout(() => {
                  taRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
                }, 280);
              } catch { /* noop */ }
            }}
            placeholder={hasStage ? "Add a caption (optional)…" : "Type a message…"}
            disabled={disabled || busy}
            className="hv-input flex-1 text-sm resize-none py-2"
            data-testid="chat-text-input"
          />
          {sendActive ? (
            <button
              type="button"
              onClick={sendNow}
              disabled={disabled || busy}
              className="hv-btn hv-btn-primary text-xs shrink-0"
              data-testid="chat-send-text"
              title={hasStage ? `Send ${stage.length} attachment${stage.length === 1 ? "" : "s"}${canSendByText ? " + caption" : ""}` : "Send"}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          ) : (
            <button
              type="button"
              onClick={onMicTap}
              disabled={disabled || busy || transcribing}
              className="hv-btn hv-btn-primary text-xs shrink-0"
              title="Tap to start, tap again to stop · 15-min cap"
              data-testid="chat-mic-btn"
            >
              <Mic className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
